import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaTrash } from "react-icons/fa";

const ListaClientes = () => {
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState({
    nombre: "",
    apellido: "",
    cedula: "",
    telefono: "",
    nacionalidad: "",
    descripcion: "",
  });

  useEffect(() => {
    const fetchClientes = async (retries = 3) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/clientes`
        );

        // Verificar que la respuesta es un arreglo
        if (Array.isArray(response.data)) {
          setClientes(response.data);
          setFilteredClientes(response.data);
        } else {
          // Si no es un arreglo, ver qué estructura tiene
          setError("Error: Los datos no son un arreglo.");
        }

        setLoading(false);
      } catch (err) {
        if (retries > 0) {
          // Retry fetching data
          setTimeout(() => fetchClientes(retries - 1), 2000);
        } else {
          setError(
            "Error al cargar los clientes. Por favor, intente nuevamente."
          );
          setLoading(false);
        }
      }
    };
    fetchClientes();
  }, []);

  // Función para sanitizar el input, eliminando posibles etiquetas de script o caracteres peligrosos
  const sanitizeInput = (input) => {
    const tempElement = document.createElement("div"); // Crear un elemento temporal
    tempElement.textContent = input; // Establecer el texto del input como contenido
    return tempElement.innerHTML; // Devolver el contenido como texto plano
  };

  // Maneja los cambios en los filtros y aplica sanitización al valor de entrada
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value); // Sanitizar el valor del input
    setFiltros((prevFiltros) => ({
      ...prevFiltros,
      [name]: sanitizedValue, // Guardar el valor sanitizado
    }));
    filtrarClientes({ ...filtros, [name]: sanitizedValue });
  };

  // Filtra los clientes en base a los filtros sanitizados
  const filtrarClientes = (filtros) => {
    const clientesFiltrados = clientes.filter((cliente) =>
      Object.keys(filtros).every((key) =>
        (cliente[key] ? cliente[key].toString().toLowerCase() : "").includes(
          filtros[key].toLowerCase()
        )
      )
    );
    setFilteredClientes(clientesFiltrados);
  };

  // Función para eliminar un cliente con una confirmación de SweetAlert
  const eliminarCliente = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "No podrás revertir esta acción.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/clientes/${id}`);
        setClientes(clientes.filter((cliente) => cliente.id !== id));
        setFilteredClientes(
          filteredClientes.filter((cliente) => cliente.id !== id)
        );
        Swal.fire(
          "Eliminado",
          "El cliente ha sido eliminado con éxito.",
          "success"
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response && error.response.status === 400
            ? "No se puede eliminar este cliente porque está asociado a una reserva"
            : "No se pudo eliminar el cliente",
          "error"
        );
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600 text-lg animate-pulse">
          Cargando clientes...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-600 text-lg">{error}</p>
      </div>
    );
  }

  if (clientes.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600 text-lg">No hay clientes registrados.</p>
      </div>
    );
  }

  return (
    <section className="min-h-screen flex flex-col items-center p-8 bg-gray-100">
      <div className="w-full max-w-6xl p-6 border rounded-lg shadow-lg bg-white">
        <h2 className="text-3xl font-extrabold text-center mb-6 tracking-wide text-gray-800">
          Listado de Clientes
        </h2>

        {/* Filtros para buscar clientes */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {Object.keys(filtros).map((key) => (
            <input
              key={key}
              type="text"
              name={key}
              placeholder={`Filtrar por ${
                key.charAt(0).toUpperCase() + key.slice(1)
              }`}
              value={filtros[key]}
              onChange={handleFilterChange} // Llama a la función de cambio de filtro con sanitización
              className="border rounded-md p-2 focus:outline-none focus:ring focus:border-blue-300"
            />
          ))}
        </div>

        {/* Tabla de Clientes */}
        <div className="overflow-x-auto">
          <table className="table-auto w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="border px-4 py-2">Nombre</th>
                <th className="border px-4 py-2">Cédula</th>
                <th className="border px-4 py-2">Fecha de Nacimiento</th>
                <th className="border px-4 py-2">Teléfono</th>
                <th className="border px-4 py-2">Nacionalidad</th>
                <th className="border px-4 py-2">Descripción</th>
                <th className="border px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredClientes.map((cliente, index) => (
                <tr
                  key={index}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  } hover:bg-gray-100`}
                >
                  <td className="border px-4 py-2">
                    {cliente.nombre} {cliente.apellido || ""}
                  </td>
                  <td className="border px-4 py-2">{cliente.cedula || ""}</td>
                  <td className="border px-4 py-2">
                    {cliente.fecha_nacimiento && new Date(cliente.fecha_nacimiento).toLocaleDateString(
                      "es-ES"
                    )}
                  </td>
                  <td className="border px-4 py-2">{cliente.telefono || ""}</td>
                  <td className="border px-4 py-2">{cliente.nacionalidad || ""}</td>
                  <td className="border px-4 py-2">
                    {cliente.descripcion && <DescripcionExpandable descripcion={cliente.descripcion} />}
                  </td>
                  <td className="border px-4 py-2">
                    <button
                      onClick={() => eliminarCliente(cliente.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Eliminar"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

// Componente de Descripción Expandible
const DescripcionExpandable = ({ descripcion }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <p>
        {isExpanded
          ? descripcion
          : descripcion?.slice(0, 50) + (descripcion?.length > 50 ? "..." : "")}
      </p>
      {descripcion && descripcion.length > 50 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-blue-500 hover:underline text-sm"
        >
          {isExpanded ? "Ver menos" : "Ver más"}
        </button>
      )}
    </div>
  );
};

export default ListaClientes;
